import React, { Fragment, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { paymentProviderApi } from '@app/store';
import { IPaymentProvider, PaymentProviderDTO } from '@app/common';
import { Button, CardButtonActionMode, Form, Panel } from '@app/components';
import { PaymentProviderForm } from './PaymentProviderForm';
import { IDashboardPaymentProvider, PaymentProviderFormValues, transformPaymentProviderFormValuesToPayload, transformToUpdatableFormPaymentProvider } from './payment-provider-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { PaymentProviderCard } from './PaymentProviderCard';
import { CheckPermissions } from '@app/components/CheckPermissions';

interface PaymentProviderUpdateProps {
  paymentProvider?: Partial<IDashboardPaymentProvider>;
  isFetching: boolean;
  toggle: (mode: CardButtonActionMode) => void;
  onDelete: (paymentProvider: Partial<IPaymentProvider>) => void;
}

export const PaymentProviderUpdate = ({ paymentProvider, isFetching, toggle, onDelete }: PaymentProviderUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const [update, { isLoading, error: apiErrors }] = paymentProviderApi.useUpdateOnePaymentProvidersMutation();

  const formMethods = useForm<PaymentProviderFormValues>({
    resolver: classValidatorResolver(PaymentProviderDTO),
    defaultValues: transformToUpdatableFormPaymentProvider(paymentProvider)
  });

  const onUpdate = async (formValues: PaymentProviderFormValues) => {
    try {
      const updatedPaymentProvider = await update({
        _id: paymentProvider?._id!,
        ...transformPaymentProviderFormValuesToPayload(formValues)
      }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedPaymentProvider });

      toast.success('Votre configuration de prestataire de paiement a bien été modifié.')
    } catch {
      toast.error('Une erreur est survenue lors de la modification de votre configuration de prestataire de paiement.')
    }
  }

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormPaymentProvider(paymentProvider));
  }, [paymentProvider, formMethods.reset]);

  return (
    <Fragment>
      <PaymentProviderCard
        paymentProvider={paymentProvider}
        formMethods={formMethods}
        toggle={toggle} />

      <Panel.Content>

        <Form formMethods={formMethods} apiErrors={apiErrors} isLoading={isFetching}>

          <Panel.FormContainer>

            <CheckPermissions permissions={['dashboard::payment-providers.delete']}>
              <Panel.Header>
                <Button
                  title="Supprimer"
                  disabled={!paymentProvider}
                  bordered
                  important
                  onPress={() => onDelete(paymentProvider!)} />
              </Panel.Header>
            </CheckPermissions>

            <PaymentProviderForm isEditing />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              disabled={isFetching}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>
        </Form>
      </Panel.Content>
    </Fragment>
  );
};
